import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  GridItem,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  VStack,
  HStack,
  Badge,
  Progress,
  useColorModeValue,
  Icon,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Skeleton,
  SkeletonText,
  Tooltip,
  Flex,
  Divider,
  List,
  ListItem,
  ListIcon,
  IconButton,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import {
  FiDollarSign,
  FiTrendingUp,
  FiUsers,
  FiPieChart,
  FiClock,
  FiBarChart2,
  FiRefreshCw,
  FiCopy,
  FiCheck,
  FiAlertTriangle,
  FiArrowUp,
  FiArrowDown,
  FiExternalLink,
  FiGift,
  FiActivity,
  FiSettings,
  FiArrowLeft,
  FiBell,
} from 'react-icons/fi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { useAccount, useBalance, useContractRead } from 'wagmi';
import { useAppSelector } from '@/store';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ConnectButton from '@/components/common/ConnectButton';
import PageHeader from '@/components/common/PageHeader';
import { usePriceAlert } from '@/hooks/usePriceAlert';

import { formatDOR, formatUSD, formatNumberWithCommas, formatAddress } from '@/utils/formatters';

// 模拟价格数据
const priceData = [
  { time: '00:00', price: 1.02, volume: 1200 },
  { time: '04:00', price: 0.98, volume: 800 },
  { time: '08:00', price: 1.01, volume: 1500 },
  { time: '12:00', price: 0.99, volume: 2000 },
  { time: '16:00', price: 1.00, volume: 1800 },
  { time: '20:00', price: 1.01, volume: 1600 },
  { time: '24:00', price: 0.99, volume: 1400 },
];

// 模拟活动数据
const activityData = [
  {
    id: 1,
    type: 'invite',
    title: 'invitedUser',
    description: 'receivedReward',
    time: 'minutesAgo',
    icon: FiGift,
    color: 'green',
  },
  {
    id: 2,
    type: 'system',
    title: 'reserveBuyback',
    description: 'destroyedDOR',
    time: 'minutesAgo',
    icon: FiActivity,
    color: 'blue',
  },
  {
    id: 3,
    type: 'recharge',
    title: 'rechargeSuccess',
    description: '100 USDT → 99.5 DOR',
    time: 'minutesAgo',
    icon: FiDollarSign,
    color: 'purple',
  },
];

const Dashboard: React.FC = () => {
  const { address, isConnected } = useAccount();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation('dashboard');
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // 价格提醒状态
  const { settings: priceAlertSettings, isMonitoring } = usePriceAlert();

  // 模拟数据 - 实际项目中应该从合约或API获取
  const mockData = {
    // 用户资产
    userAssets: {
      circulatingDOR: 150.00,
      lockedDOR: 200.00,
      totalDOR: 350.00,
      estimatedValue: 345.00,
      lockRatio: 57, // 锁定比例
    },
    // 系统状态
    systemStats: {
      totalSupply: 1000000000, // 10亿DOR
      circulatingSupply: 750000000, // 7.5亿流通
      reserveUSDT: 500000000, // 5亿储备
      reserveRatio: 50, // 储备背书比率
      currentPrice: 0.99, // 当前价格
      priceChange24h: -1.2, // 24小时价格变化
      marketCap: 990000000, // 市值
      activeUsers: 1234, // 活跃用户
    },
    // 邀请统计
    inviteStats: {
      totalInvites: 12,
      activeInvites: 8,
      totalRewards: 45.67,
      pendingRewards: 12.34,
    },
  };

  // 复制地址
  const copyAddress = () => {
    if (address) {
      navigator.clipboard.writeText(address);
      setCopied(true);
      toast({
        title: t('addressCopied'),
        description: t('addressCopiedDescription'),
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // 刷新数据
  const refreshData = () => {
    setIsLoading(true);
    // 模拟数据刷新
    setTimeout(() => {
      setIsLoading(false);
      toast({
        title: t('dataUpdated'),
        description: t('dataUpdatedDescription'),
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    }, 1000);
  };

  // 如果未连接钱包，显示连接提示
  if (!isConnected) {
    return (
      <Box>
        <VStack spacing={{ base: 4, md: 6 }} align="stretch">
          {/* 顶部区域 */}
          <PageHeader title={t('title')} />
          
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardBody textAlign="center" py={{ base: 8, md: 12 }}>
                              <Box mb={4}>
                  <FiDollarSign size={64} color="var(--chakra-colors-blue-500)" />
                </Box>
              <Heading size={{ base: "sm", md: "md" }} mb={2}>
                {t('connectWalletPrompt')}
              </Heading>
              <Text color="gray.500" mb={6} fontSize={{ base: "sm", md: "md" }}>
                {t('connectWalletDescription')}
              </Text>
              <ConnectButton />
            </CardBody>
          </Card>
        </VStack>
      </Box>
    );
  }

  return (
    <Box pt={4}>
      <VStack spacing={{ base: 4, md: 6 }} align="stretch">
        {/* 顶部区域 - 用户信息和警报 */}
        <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={{ base: 4, md: 6 }}>
          {/* 用户信息 */}
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardBody>
              <VStack spacing={3} align="stretch">
                <HStack justify="space-between" align="center">
                  <VStack align="flex-start" spacing={1}>
                    <Text fontSize="sm" color="gray.500">{t('walletAddress')}</Text>
                    <HStack>
                      <Text fontWeight="bold" fontFamily="mono" fontSize={{ base: "xs", md: "sm" }}>
                        {formatAddress(address || '')}
                      </Text>
                      <IconButton
                        size="sm"
                        variant="ghost"
                        icon={copied ? <FiCheck /> : <FiCopy />}
                        onClick={copyAddress}
                        aria-label={t('copyAddress')}
                      />
                    </HStack>
                  </VStack>
                  <Badge colorScheme="green" size={{ base: "sm", md: "md" }}>{t('bscTestnet')}</Badge>
                </HStack>
              </VStack>
            </CardBody>
          </Card>
        </Grid>

        {/* 主体内容区 - 电脑端并排显示，移动端垂直堆叠 */}
        <Grid templateColumns={{ base: '1fr', xl: '1fr 1fr' }} gap={{ base: 4, md: 6 }}>
          {/* 用户资产概览 */}
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardHeader>
              <HStack justify="space-between">
                <Heading size="md">{t('yourDORAssets')}</Heading>
              </HStack>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                {/* 资产统计 */}
                <VStack spacing={3} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.500">{t('circulatingDOR')}</Text>
                    <Tooltip label={t('circulatingTooltip')}>
                      <Text fontWeight="bold" color="green.500">
                        {formatDOR(mockData.userAssets.circulatingDOR)}
                      </Text>
                    </Tooltip>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.500">{t('lockedDOR')}</Text>
                    <Tooltip label={t('lockedTooltip')}>
                      <Text fontWeight="bold" color="orange.500">
                        {formatDOR(mockData.userAssets.lockedDOR)}
                      </Text>
                    </Tooltip>
                  </HStack>
                  <Divider />
                  <HStack justify="space-between">
                    <Text fontSize="lg" fontWeight="bold">{t('total')}</Text>
                    <Text fontSize="lg" fontWeight="bold">
                      {formatDOR(mockData.userAssets.totalDOR)}
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.500">{t('currentValuation')}</Text>
                    <Text fontWeight="bold" color="blue.500">
                      {formatUSD(mockData.userAssets.estimatedValue)}
                    </Text>
                  </HStack>
                </VStack>

                {/* 锁定比例进度条 */}
                <Box>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm">{t('lockRatio')}</Text>
                    <Text fontSize="sm" fontWeight="bold">{mockData.userAssets.lockRatio}%</Text>
                  </HStack>
                  <Progress 
                    value={mockData.userAssets.lockRatio} 
                    colorScheme="orange" 
                    size="sm"
                    borderRadius="full"
                  />
                </Box>

                {/* 行动按钮 - 移动端垂直排列 */}
                <VStack spacing={3}>
                  <Button 
                    size="md" 
                    colorScheme="blue" 
                    w="full"
                    onClick={() => navigate('/management')}
                  >
                    {t('manageDOR')}
                  </Button>
                  <Button size="md" variant="outline" w="full">
                    {t('sellCirculating')}
                  </Button>
                </VStack>
              </VStack>
            </CardBody>
          </Card>

          {/* 系统状态概览 */}
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardHeader>
              <Heading size="md">{t('systemOverview')}</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                {/* 关键指标 */}
                <VStack spacing={3} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.500">{t('totalSupply')}</Text>
                    <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>
                      {formatDOR(mockData.systemStats.totalSupply)}
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.500">{t('reserveUSDT')}</Text>
                    <Tooltip label={t('reserveTooltip')}>
                      <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>
                        {formatUSD(mockData.systemStats.reserveUSDT)}
                      </Text>
                    </Tooltip>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.500">{t('reserveBacking')}</Text>
                    <Text 
                      fontWeight="bold" 
                      fontSize={{ base: "sm", md: "md" }}
                      color={mockData.systemStats.reserveRatio > 80 ? 'green.500' : 
                             mockData.systemStats.reserveRatio < 50 ? 'red.500' : 'orange.500'}
                    >
                      {mockData.systemStats.reserveRatio}%
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.500">{t('priceAnchor')}</Text>
                    <Text 
                      fontWeight="bold" 
                      fontSize={{ base: "sm", md: "md" }}
                      color={mockData.systemStats.currentPrice >= 1 ? 'green.500' : 'red.500'}
                    >
                      {mockData.systemStats.currentPrice} USDT
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.500">{t('activeUsers')}</Text>
                    <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>
                      {formatNumberWithCommas(mockData.systemStats.activeUsers)}
                    </Text>
                  </HStack>
                </VStack>

                {/* 状态徽章 */}
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.500">{t('systemStatus')}</Text>
                  <Badge 
                    colorScheme={mockData.systemStats.currentPrice >= 0.95 ? 'green' : 'yellow'}
                    size={{ base: "sm", md: "md" }}
                  >
                    {mockData.systemStats.currentPrice >= 0.95 ? t('stable') : t('deAnchorRisk')}
                  </Badge>
                </HStack>

                {/* 行动按钮 - 移动端垂直排列 */}
                <VStack spacing={3}>
                  <Button 
                    size="md" 
                    colorScheme="blue" 
                    w="full"
                    onClick={() => navigate('/reserve')}
                  >
                    {t('viewReserveDetails')}
                  </Button>
                  <Button size="md" variant="outline" w="full">
                    {t('participateGovernance')}
                  </Button>
                </VStack>
              </VStack>
            </CardBody>
          </Card>
                </Grid>

        {/* 其他内容区域 */}
        <VStack spacing={{ base: 4, md: 6 }} align="stretch">
          {/* 价格与市场图表 - 移动端隐藏图表 */}
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardHeader>
              <Heading size="md">{t('marketDynamics')}</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                {/* 实时价格 */}
                <Box textAlign="center">
                  <HStack justify="center" spacing={2} mb={2}>
                    <Text fontSize="sm" color="gray.500">{t('currentPrice')}</Text>
                    {isMonitoring && (
                      <Tooltip 
                        label={`价格提醒已启用，阈值：${priceAlertSettings?.lowThreshold || 0.95} - ${priceAlertSettings?.highThreshold || 1.05} USDT`}
                        placement="top"
                      >
                        <Box>
                          <FiBell color="var(--chakra-colors-blue-500)" size="16px" />
                        </Box>
                      </Tooltip>
                    )}
                  </HStack>
                  <HStack justify="center" spacing={2}>
                    <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">
                      {mockData.systemStats.currentPrice} USDT
                    </Text>
                    {mockData.systemStats.priceChange24h >= 0 ? (
                      <FiArrowUp color="var(--chakra-colors-green-500)" />
                    ) : (
                      <FiArrowDown color="var(--chakra-colors-red-500)" />
                    )}
                    <Text 
                      fontSize={{ base: "md", md: "lg" }}
                      color={mockData.systemStats.priceChange24h >= 0 ? 'green.500' : 'red.500'}
                    >
                      {mockData.systemStats.priceChange24h > 0 ? '+' : ''}
                      {mockData.systemStats.priceChange24h}%
                    </Text>
                  </HStack>
                </Box>

                {/* 价格图表 - 移动端隐藏 */}
                <Box h="200px" display={{ base: "none", md: "block" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={priceData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" />
                      <YAxis domain={[0.95, 1.05]} />
                      <RechartsTooltip />
                      <Area 
                        type="monotone" 
                        dataKey="price" 
                        stroke="#3182ce" 
                        fill="#3182ce" 
                        fillOpacity={0.3}
                      />
                      {/* 锚定线 */}
                      <Line 
                        type="monotone" 
                        dataKey={() => 1} 
                        stroke="#e53e3e" 
                        strokeDasharray="5 5"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>

                {/* 市场指标 */}
                <VStack spacing={2} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.500">{t('priceChange24h')}</Text>
                    <Text 
                      fontWeight="bold"
                      fontSize={{ base: "sm", md: "md" }}
                      color={mockData.systemStats.priceChange24h >= 0 ? 'green.500' : 'red.500'}
                    >
                      {mockData.systemStats.priceChange24h > 0 ? '+' : ''}
                      {mockData.systemStats.priceChange24h}%
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.500">{t('marketCap')}</Text>
                    <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>
                      {formatUSD(mockData.systemStats.marketCap)}
                    </Text>
                  </HStack>
                </VStack>

                {/* 套利提示 */}
                {mockData.systemStats.currentPrice > 1 && (
                  <Alert status="info" size="sm">
                    <AlertIcon />
                    <Box>
                      <AlertTitle fontSize="sm">{t('rechargeOpportunity')}</AlertTitle>
                      <AlertDescription fontSize="xs">
                        {t('potentialProfitText')} {(mockData.systemStats.currentPrice - 1) * 100}%
                      </AlertDescription>
                    </Box>
                  </Alert>
                )}

                {/* 行动按钮 - 移动端垂直排列 */}
                <Button 
                  size="md" 
                  colorScheme="blue" 
                  leftIcon={<FiExternalLink />}
                  w="full"
                  onClick={() => window.open('https://pancakeswap.finance/swap', '_blank')}
                >
                  {t('externalTrading')}
                </Button>
              </VStack>
            </CardBody>
          </Card>
        </VStack>

        {/* 最近活动 */}
        <Card bg={cardBg} border="1px" borderColor={borderColor}>
          <CardHeader>
            <Heading size="md">{t('recentActivity')}</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={3} align="stretch">
              {activityData.map((activity) => (
                <Box key={activity.id} p={3} borderWidth="1px" borderRadius="md">
                  <HStack spacing={3}>
                    <Box as={activity.icon} color={`${activity.color}.500`} />
                    <VStack align="flex-start" spacing={1} flex={1}>
                      <Text fontWeight="medium" fontSize={{ base: "sm", md: "md" }}>
                        {t(activity.title)}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {t(activity.description)} • {t(activity.time)}
                      </Text>
                    </VStack>
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={<FiExternalLink />}
                      aria-label={t('viewDetails')}
                      onClick={() => {
                        if (activity.type === 'invite') {
                          navigate('/invite');
                        } else if (activity.type === 'recharge') {
                          navigate('/recharge');
                        } else {
                          navigate('/reserve');
                        }
                      }}
                    />
                  </HStack>
                </Box>
              ))}
            </VStack>
          </CardBody>
        </Card>

        {/* 底部快速操作 - 移动端垂直排列 */}
        <Card bg={cardBg} border="1px" borderColor={borderColor}>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.500" textAlign="center">
                {t('dorVersion')} | {t('poweredBy')}
              </Text>
              <VStack spacing={3}>
                <Button 
                  size="md" 
                  colorScheme="blue" 
                  leftIcon={<FiDollarSign />}
                  w="full"
                  onClick={() => navigate('/recharge')}
                >
                  {t('rechargeDOR')}
                </Button>
                <Button 
                  size="md" 
                  variant="outline" 
                  leftIcon={<FiUsers />}
                  w="full"
                  onClick={() => navigate('/invite')}
                >
                  {t('generateInviteCode')}
                </Button>
                <Button 
                  size="md" 
                  variant="outline" 
                  leftIcon={<FiBarChart2 />}
                  w="full"
                  onClick={() => navigate('/reserve')}
                >
                  {t('viewReserve')}
                </Button>
              </VStack>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
};

export default Dashboard; 